import {
  type IGarmentPieceComponent,
  type PieceButton,
  type PieceLogo,
  GarmentPieceFabrics,
} from '@seddi/scene';
import { BufferGeometry } from '@seddi/three';

import { ArrayDiff } from '../core/reconciler/arrayDiff';
import { PropsDiff } from '../core/reconciler/propsDiff';
import {
  computeVertexNormalsF32,
  updateIndex,
  updateVertexAttributeF32,
} from '../core/bufferGeometry';
import * as FlagsModule from '../flags/flagsModule';
import { FeatFlag } from '../flags/flagTypes';
import {
  type PieceButtonRenderable,
  create as createButton,
  dispose as disposeButton,
} from '../renderables/pieceButtonRenderable';
import {
  type PieceLogoRenderable,
  create as createLogo,
  dispose as disposeLogo,
} from '../renderables/pieceLogoRenderable';
import {
  type PieceRenderable,
  attachFabric,
  detachFabric,
  recreateGroups,
  recreateMaterials,
  updateGeometryFromGarment,
  updateGeometryWireframe,
  updateGrain,
} from '../renderables/pieceRenderable';
import { getFabricColorId } from '../resources/fabric';
import type { FabricWithColor } from '../resources/fabricTypes';
import type { GarmentGeometryDesc } from '../resources/garmentGeometryTypes';
import type { TPieceGeometryEx } from '../scene/sceneTypes';
import {
  update1st as updateButton1st,
  update2nd as updateButton2nd,
} from './pieceButtonReconciler';
import { update as updateLogo } from './pieceLogoReconciler';
import type { SceneReconcilerFrame } from './sceneReconcilerFrame';

/**
 * Reconcile
 * @param renderable - Renderable
 * @param nextProps - Piece next.
 * @param prevProps - Piece prev.
 * @param frame - Frame.
 */
export const update1st = (
  renderable: PieceRenderable,
  nextProps: IGarmentPieceComponent,
  prevProps: IGarmentPieceComponent | undefined,
  frame: SceneReconcilerFrame,
): boolean => {
  let dirty = false;

  const isGeometryDiff = renderable.garmentGeometry !== frame.garmentGeometry;
  const isGeometryDiffDw = renderable.garmentGeometry && !frame.garmentGeometry;

  const diffProps = PropsDiff.diff(nextProps, prevProps);
  let isGeometryDirty = false;

  if (diffProps.includes('geometry') || isGeometryDiff) {
    updateGeometry(renderable, nextProps, prevProps, frame.garmentGeometry, isGeometryDiffDw);
    isGeometryDirty = true;
    frame.entities.get(renderable.id)!.isGeometryDirty = true;
    dirty = true;
  }
  // Fabric affects materials.
  if (diffProps.includes('fabric') || diffProps.includes('fabricColor')) {
    dirty = updateFabric(renderable, nextProps, prevProps, frame) || dirty;
  }
  // Grain affects materials.
  if (diffProps.includes('grain') && renderable.fabricRef) {
    dirty = updateGrain(renderable, nextProps.grain) || dirty;
  }
  // Logos affects materials.
  if (diffProps.includes('logos')) {
    dirty = updateLogos(renderable, nextProps.logos, prevProps?.logos, frame) || dirty;
  }
  // Buttons placement depends on piece geometry.
  if (diffProps.includes('buttons') || isGeometryDirty) {
    dirty = updateButtons1st(renderable, nextProps.buttons, prevProps?.buttons, frame) || dirty;
  }

  return dirty;
};

/**
 * Reconcile
 * @param renderable - Renderable
 * @param nextProps - Piece next.
 * @param prevProps - Piece prev.
 * @param frame - Frame.
 */
export const update2nd = (
  renderable: PieceRenderable,
  nextProps: IGarmentPieceComponent,
  prevProps: IGarmentPieceComponent | undefined,
  frame: SceneReconcilerFrame,
): boolean => {
  let dirty = false;

  // Buttons placement depends on hole parents being updated first.
  dirty = updateButtons2nd(renderable, nextProps.buttons, frame) || dirty;

  // dirty =
  //   ThreeButtonsLinesReconciler.Update(
  //     scene,
  //     nextScene,
  //     prevScene,
  //     nextShowGarmentGeometry,
  //     this,
  //   ) || dirty;

  return dirty;
};

/**
 * Update fabric.
 * @param renderable - Renderable.
 * @param nextProps - Piece component prev.
 * @param prevProps - Piece component next.
 * @param frame - Frame.
 */
const updateFabric = (
  renderable: PieceRenderable,
  nextProps: IGarmentPieceComponent | undefined,
  prevProps: IGarmentPieceComponent | undefined,
  frame: SceneReconcilerFrame,
): boolean => {
  const { resources, nextScene } = frame;

  if (prevProps) {
    detachFabric(renderable, resources);
  }
  if (!nextProps) {
    return true;
  }

  const fabricColorId = getFabricColorId(nextProps.fabric, nextProps.fabricColor);
  let dirty = false;

  if (resources.findFabric(fabricColorId)) {
    attachFabric(renderable, fabricColorId, nextProps.grain, frame.resources);
    dirty = true;
  } else {
    if (!resources.hasFabricRequest(fabricColorId)) {
      const fabric = GarmentPieceFabrics.FindFabricInScene(nextScene, nextProps.fabric);
      const fabricWithColor: FabricWithColor = {
        ...fabric,
        id: fabricColorId,
        recolorTarget: nextProps.fabricColor,
      };
      resources.loadFabricDeferred(fabricWithColor);
    }
  }

  return dirty;
};

/**
 * Update geometry from low-res.
 * @param renderable - Renderable.
 * @param next - Piece next.
 * @param prev - Piece prev.
 */
const updateGeometryFromLoRes = (
  renderable: PieceRenderable,
  next: TPieceGeometryEx,
  prev: TPieceGeometryEx | undefined,
) => {
  const diff = PropsDiff.diff(next, prev);

  const geometry = renderable.mesh.geometry as BufferGeometry;

  if (diff.includes('indices')) {
    updateIndex(geometry, next.indices);
  }
  if (diff.includes('position2D')) {
    updateVertexAttributeF32(geometry, 'uv', next.position2D, 2);
  }
  if (diff.includes('position3D')) {
    updateVertexAttributeF32(geometry, 'position', next.position3D, 3);
    if (next.normal) {
      updateVertexAttributeF32(geometry, 'normal', next.normal, 3);
    } else {
      computeVertexNormalsF32(geometry);
    }
  }

  if (FlagsModule.isEnabled(FeatFlag.WIREFRAME_FABRICS)) {
    updateGeometryWireframe(renderable, next);
  }

  renderable.garmentGeometry = undefined;
};

/**
 * Updates renderable geometry.
 * @param renderable - Renderable.
 * @param next - Piece next.
 * @param prev - Piece prev.
 * @param garmentGeometry - Garment geometry.
 * @param force - Force update.
 */
export const updateGeometry = (
  renderable: PieceRenderable,
  next: IGarmentPieceComponent,
  prev: IGarmentPieceComponent | undefined,
  garmentGeometry: GarmentGeometryDesc | undefined,
  force?: boolean,
): boolean => {
  if (garmentGeometry) {
    updateGeometryFromGarment(renderable, garmentGeometry);
  } else {
    const prev_ = force ? undefined : prev;
    updateGeometryFromLoRes(renderable, next.geometry, prev_?.geometry);
  }

  const geometry = renderable.geometry;
  geometry.computeBoundingSphere();
  recreateGroups(renderable);

  return true;
};

/**
 * Update buttons.
 * @param renderable - Renderable.
 * @param nextProps - Buttons next.
 * @param prevProps - Buttons prev.
 * @param frame - Frame.
 */
const updateButtons1st = (
  renderable: PieceRenderable,
  nextProps: PieceButton[],
  prevProps: PieceButton[] | undefined,
  frame: SceneReconcilerFrame,
): boolean => {
  const { resources } = frame;

  const [nextDiff, prevDiff] = ArrayDiff.diff(nextProps ?? [], prevProps ?? []);

  const bs = renderable.buttons;
  const map = bs.reduce<Map<string, PieceButtonRenderable>>((map, b) => {
    map.set(b.id, b);
    return map;
  }, new Map());

  bs.length = 0;

  for (const pair of nextDiff) {
    const b = map.get(pair.next._id) ?? createButton(pair.next._id, renderable.mesh.parent);
    updateButton1st(b, pair.next, pair.prev, renderable, frame);
    bs.push(b);
  }

  for (const prev of prevDiff) {
    const b = map.get(prev._id);
    disposeButton(b, resources);
  }

  return true;
};

/**
 * Update buttons.
 * @param renderable - Renderable.
 * @param props - Buttons next.
 * @param frame - Frame.
 */
const updateButtons2nd = (
  renderable: PieceRenderable,
  props: PieceButton[],
  frame: SceneReconcilerFrame,
): boolean => {
  const bs = renderable.buttons;
  const map = bs.reduce<Map<string, PieceButtonRenderable>>((map, b) => {
    map.set(b.id, b);
    return map;
  }, new Map());

  for (const button of props) {
    const buttonRenderable = map.get(button._id)!;
    updateButton2nd(buttonRenderable, button, renderable, frame);
  }

  return true;
};

/**
 * Update logos.
 * @param renderable - Renderable.
 * @param nextProps - Logos next.
 * @param prevProps - Logos prev.
 * @param frame - Frame.
 */
const updateLogos = (
  renderable: PieceRenderable,
  nextProps: PieceLogo[],
  prevProps: PieceLogo[] | undefined,
  frame: SceneReconcilerFrame,
): boolean => {
  const { resources } = frame;

  const [nextDiff, prevDiff] = ArrayDiff.diff(nextProps ?? [], prevProps ?? []);

  const ls = renderable.logos;
  const map = ls.reduce<Map<string, PieceLogoRenderable>>((map, l) => {
    map.set(l.id, l);
    return map;
  }, new Map());

  ls.length = 0;

  for (const pair of nextDiff) {
    const l = map.get(pair.next._id) ?? createLogo(pair.next._id);
    updateLogo(l, pair.next, pair.prev, frame);
    ls.push(l);
  }

  for (const prev of prevDiff) {
    const l = map.get(prev._id);
    disposeLogo(l, resources);
  }

  recreateMaterials(renderable);
  recreateGroups(renderable);

  return true;
};
